import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import RooftopViewer3D from '../components/RooftopViewer3D';
import Logo from '../components/Logo';
import { COMPANY, inr } from '../mock';
import {
  Sun, Zap, IndianRupee, Leaf, TrendingUp, CheckCircle2, Loader2, FileText,
  Phone, Mail, MapPin, Download, MessageCircle, Box,
} from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function PublicProposal() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/public/proposals/${id}`);
        setP(r.data);
      } catch (e) {
        setError(e?.response?.status === 404 ? 'Proposal not found' : 'Unable to load proposal');
      } finally { setLoading(false); }
    })();
  }, [id]);

  const downloadPdf = () => {
    window.open(`${API}/public/proposals/${id}/pdf`, '_blank');
  };
  const callSales = () => { window.location.href = `tel:${COMPANY.phone.replace(/\s/g, '')}`; };
  const whatsappSales = () => {
    const msg = encodeURIComponent(`Hi Inpace Power team, I'd like to proceed with my solar proposal ${id}.`);
    const num = COMPANY.phone.replace(/[^0-9]/g, '');
    window.open(`https://wa.me/${num}?text=${msg}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-emerald-50">
        <div className="text-emerald-700 flex items-center"><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading your proposal…</div>
      </div>
    );
  }
  if (error || !p) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-emerald-50 p-6">
        <Card className="p-8 max-w-md text-center border-emerald-100">
          <FileText className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <h1 className="text-xl font-bold text-emerald-950">{error || 'Proposal not found'}</h1>
          <p className="text-slate-500 mt-2 text-sm">Please check the link or contact Inpace Power.</p>
          <Button onClick={callSales} className="mt-4 bg-emerald-700 text-white"><Phone className="w-4 h-4 mr-2" /> Call us</Button>
        </Card>
      </div>
    );
  }

  const d = p.design3d || { rows: 4, cols: 6, tilt: p.tilt || 15, azimuth: 180, panel_w: 1.13, panel_h: 2.28, polygon: null };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-emerald-50/30 pb-20">
      {/* Header */}
      <header className="bg-white border-b border-emerald-100 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <Logo />
          <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Customer Proposal</Badge>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-5 lg:px-8 pt-10">
        <div className="text-center max-w-2xl mx-auto">
          <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
            <Sun className="w-3.5 h-3.5 mr-1.5" /> Solar Proposal {p.id}
          </Badge>
          <h1 className="mt-4 text-4xl lg:text-5xl font-extrabold text-emerald-950 leading-tight">
            Hi {p.customer.split(' ')[0]}, here's your <span className="text-amber-600">{p.kw} kW</span> solar plan
          </h1>
          <p className="mt-4 text-slate-600">A custom design for your rooftop at <strong>{p.address}</strong>, prepared by Inpace Power. Drag the 3D model below to explore.</p>
        </div>
      </section>

      {/* 3D Viewer */}
      <section className="max-w-5xl mx-auto px-5 lg:px-8 mt-8">
        <Card className="overflow-hidden border-emerald-100 shadow-xl">
          <div className="px-5 py-3 bg-emerald-50/60 border-b border-emerald-100 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              <Box className="w-4 h-4 text-emerald-700" />
              <span className="font-semibold text-emerald-900">Your Rooftop in 3D</span>
            </div>
            <div className="text-xs text-emerald-700">{(d.rows || 4)} × {(d.cols || 6)} layout · {p.numPanels} panels · {p.kw} kW</div>
          </div>
          <RooftopViewer3D
            polygon={d.polygon}
            rows={d.rows}
            cols={d.cols}
            tilt={d.tilt}
            azimuth={d.azimuth}
            panelW={d.panel_w}
            panelH={d.panel_h}
            height="500px"
            autoRotate
          />
        </Card>
      </section>

      {/* KPI tiles */}
      <section className="max-w-5xl mx-auto px-5 lg:px-8 mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { i: Zap, l: 'Yearly Generation', v: (p.yearlyUnits || 0).toLocaleString('en-IN') + ' kWh', c: 'from-amber-100 to-amber-50 text-amber-700' },
          { i: IndianRupee, l: 'Yearly Savings', v: inr(p.yearlySavings), c: 'from-emerald-100 to-emerald-50 text-emerald-700' },
          { i: TrendingUp, l: 'Payback', v: (p.payback || 0) + ' years', c: 'from-sky-100 to-sky-50 text-sky-700' },
          { i: Leaf, l: 'CO₂ saved / year', v: (p.co2 || 0).toLocaleString('en-IN') + ' kg', c: 'from-violet-100 to-violet-50 text-violet-700' },
        ].map(k => {
          const I = k.i;
          return (
            <div key={k.l} className={`p-5 rounded-2xl bg-gradient-to-br ${k.c} border border-white/40 shadow-sm`}>
              <I className="w-6 h-6" />
              <div className="mt-3 text-2xl font-extrabold text-emerald-950">{k.v}</div>
              <div className="text-xs text-slate-600 mt-1">{k.l}</div>
            </div>
          );
        })}
      </section>

      {/* Investment block */}
      <section className="max-w-5xl mx-auto px-5 lg:px-8 mt-8">
        <Card className="p-6 border-emerald-100">
          <h2 className="text-xl font-extrabold text-emerald-950 mb-4">Your Investment</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
              <div className="text-xs text-slate-500">Gross System Cost</div>
              <div className="text-2xl font-extrabold text-slate-900">{inr(p.grossCost)}</div>
            </div>
            <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-100">
              <div className="text-xs text-emerald-700">Government Subsidy</div>
              <div className="text-2xl font-extrabold text-emerald-700">- {inr(p.subsidy)}</div>
            </div>
            <div className="p-4 rounded-xl bg-amber-50 border-2 border-amber-200">
              <div className="text-xs text-amber-800 font-bold">Net Investment</div>
              <div className="text-2xl font-extrabold text-amber-900">{inr(p.netCost)}</div>
            </div>
          </div>
          <div className="mt-4 p-4 rounded-xl bg-emerald-700 text-white text-center">
            <div className="text-xs uppercase tracking-widest text-emerald-200">Total 25-year Savings</div>
            <div className="text-3xl font-extrabold mt-1">{inr(p.lifetimeSavings)}</div>
          </div>
        </Card>
      </section>

      {/* Included */}
      <section className="max-w-5xl mx-auto px-5 lg:px-8 mt-8">
        <Card className="p-6 border-emerald-100">
          <h2 className="text-xl font-extrabold text-emerald-950 mb-4">What's Included</h2>
          <div className="grid sm:grid-cols-2 gap-y-2 gap-x-6 text-sm">
            {[
              '25-year linear performance warranty on panels',
              '10-year inverter warranty (extendable)',
              '5-year workmanship warranty by Inpace Power',
              'Free site survey & shading analysis',
              'BESCOM net-metering paperwork',
              '1 year free AMC + remote monitoring',
              'On-call support within 24 hours',
              'Capacity test report on commissioning',
            ].map(t => (
              <div key={t} className="flex items-start gap-2 text-slate-700"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> {t}</div>
            ))}
          </div>
        </Card>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-5 lg:px-8 mt-10">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-700 to-emerald-900 text-white px-8 lg:px-12 py-10">
          <div className="absolute -right-10 -top-10 w-60 h-60 rounded-full bg-amber-400/20 blur-3xl" />
          <div className="relative grid lg:grid-cols-2 gap-6 items-center">
            <div>
              <h2 className="text-3xl font-extrabold">Ready to go solar?</h2>
              <p className="mt-2 text-emerald-100">Talk to our team — most rooftops are commissioned within 7 working days.</p>
            </div>
            <div className="flex flex-wrap gap-3 lg:justify-end">
              <Button size="lg" onClick={whatsappSales} className="bg-amber-500 hover:bg-amber-600 text-emerald-950 font-semibold">
                <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp Us
              </Button>
              <Button size="lg" variant="outline" onClick={callSales} className="bg-white/10 border-white/30 text-white hover:bg-white hover:text-emerald-900">
                <Phone className="w-4 h-4 mr-2" /> {COMPANY.phone}
              </Button>
              <Button size="lg" variant="outline" onClick={downloadPdf} className="bg-white/10 border-white/30 text-white hover:bg-white hover:text-emerald-900">
                <Download className="w-4 h-4 mr-2" /> Download PDF
              </Button>
            </div>
          </div>
        </div>
      </section>

      <footer className="max-w-5xl mx-auto px-5 lg:px-8 mt-10 text-center text-xs text-slate-500">
        <div className="flex items-center justify-center gap-2 mb-2">
          <MapPin className="w-3.5 h-3.5" /> {COMPANY.address}
          <span>·</span>
          <Mail className="w-3.5 h-3.5" /> {COMPANY.email}
        </div>
        © {new Date().getFullYear()} Inpace Power · GSTIN: {COMPANY.gstin}
      </footer>
    </div>
  );
}
